import React, { useState, useEffect, useCallback } from 'react';
import { getAutoRbStatus, startAutoRb, stopAutoRb, planAutoRb, getAutoRbScheduled, getGateways } from '../api';

const fmtTime = (d) => d ? new Date(d).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '—';

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-500/10 text-yellow-400', processing: 'bg-blue-500/10 text-blue-400',
    completed: 'bg-green-500/10 text-green-400', declined: 'bg-red-500/10 text-red-400',
    no_gateway: 'bg-orange-500/10 text-orange-400', cancelled: 'bg-slate-500/10 text-slate-400',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-500/10 text-slate-400'}`}>{status?.replace(/_/g, ' ')}</span>;
};

function AutoRbTab({ socket }) {
  const [status, setStatus] = useState({ engine: { running: false }, pending_jobs: 0, processing_jobs: 0, completed_today: 0, declined_today: 0 });
  const [scheduled, setScheduled] = useState({ jobs: [], total: 0 });
  const [gateways, setGateways] = useState([]);
  const [selectedGateways, setSelectedGateways] = useState([]);
  const [actionLoading, setActionLoading] = useState('');
  const [jobFilter, setJobFilter] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [sRes, jRes, gRes] = await Promise.all([
        getAutoRbStatus(), getAutoRbScheduled(jobFilter ? `status=${jobFilter}` : ''), getGateways()
      ]);
      if (sRes.success) setStatus(sRes.data);
      if (jRes.success) setScheduled(jRes.data);
      if (gRes.success) setGateways(gRes.data);
    } catch (e) { console.error(e); }
  }, [jobFilter]);

  useEffect(() => { fetchAll(); const i = setInterval(fetchAll, 12000); return () => clearInterval(i); }, [fetchAll]);

  useEffect(() => {
    if (!socket) return;
    const r = () => fetchAll();
    socket.on('job_completed', r); socket.on('job_declined', r); socket.on('planning_complete', r);
    return () => { socket.off('job_completed', r); socket.off('job_declined', r); socket.off('planning_complete', r); };
  }, [socket, fetchAll]);

  const handleStart = async () => {
    setActionLoading('start');
    try { await startAutoRb(selectedGateways); fetchAll(); } catch (e) { console.error(e); }
    setActionLoading('');
  };
  const handleStop = async () => {
    setActionLoading('stop');
    try { await stopAutoRb(); fetchAll(); } catch (e) { console.error(e); }
    setActionLoading('');
  };
  const handlePlan = async () => {
    setActionLoading('plan');
    try { await planAutoRb(); fetchAll(); } catch (e) { console.error(e); }
    setActionLoading('');
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">AutoRb — Automatic 72h Rebill</h2>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Engine', value: status.engine?.running ? 'Running' : 'Stopped', color: status.engine?.running ? 'text-green-400' : 'text-red-400', bg: status.engine?.running ? 'bg-green-500/10' : 'bg-red-500/10' },
          { label: 'Pending Jobs', value: status.pending_jobs, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
          { label: 'Processing', value: status.processing_jobs, color: 'text-blue-400', bg: 'bg-blue-500/10' },
          { label: 'Completed Today', value: status.completed_today, color: 'text-green-400', bg: 'bg-green-500/10' },
          { label: 'Declined Today', value: status.declined_today, color: 'text-red-400', bg: 'bg-red-500/10' },
        ].map((s, i) => (
          <div key={i} className={`${s.bg} rounded-xl p-4 border border-slate-700/50`}>
            <p className="text-xs text-slate-400 mb-1">{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold text-white">AutoRb Engine Controls</h3>
          <div className="flex items-center space-x-2">
            <button onClick={handlePlan} disabled={actionLoading === 'plan'}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
              {actionLoading === 'plan' ? 'Planning...' : '📋 Plan Now'}
            </button>
            {status.engine?.running ? (
              <button onClick={handleStop} disabled={actionLoading === 'stop'}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-bold flex items-center space-x-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="1" /></svg>
                <span>{actionLoading === 'stop' ? 'Stopping...' : 'Stop AutoRb'}</span>
              </button>
            ) : (
              <button onClick={handleStart} disabled={actionLoading === 'start'}
                className="px-5 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-sm font-bold flex items-center space-x-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21" /></svg>
                <span>{actionLoading === 'start' ? 'Starting...' : 'Start AutoRb'}</span>
              </button>
            )}
          </div>
        </div>
        <div>
          <p className="text-xs text-slate-400 mb-2">Select gateways (none = all active):</p>
          <div className="flex flex-wrap gap-2">
            {gateways.filter(g => g.is_active).map(gw => (
              <button key={gw.id}
                onClick={() => setSelectedGateways(p => p.includes(gw.id) ? p.filter(x => x !== gw.id) : [...p, gw.id])}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                  selectedGateways.includes(gw.id) ? 'bg-purple-600/20 border-purple-500 text-purple-400' : 'bg-slate-700/50 border-slate-600 text-slate-400'
                }`}>{gw.processor} - {gw.bank} - {gw.dba}</button>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-3">AutoRb runs daily at midnight: scrapes last 24h from external DB + CheckoutChamp, schedules rebills 48h–4d after initial transaction.</p>
      </div>

      {/* Scheduled Jobs */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Scheduled AutoRb Jobs ({scheduled.total})</h3>
          <div className="flex space-x-1">
            {['', 'pending', 'processing', 'completed', 'declined'].map(f => (
              <button key={f} onClick={() => setJobFilter(f)}
                className={`px-3 py-1 rounded-lg text-xs font-medium ${jobFilter === f ? 'bg-purple-600 text-white' : 'bg-slate-700 text-slate-400'}`}>
                {f || 'All'}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-700/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Gateway</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Scheduled</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Attempts</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {scheduled.jobs?.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No scheduled jobs</td></tr>
              ) : scheduled.jobs?.map(j => (
                <tr key={j.id} className="hover:bg-slate-700/30">
                  <td className="px-4 py-2.5 text-white text-xs">{j.client_name} <span className="text-purple-400 font-mono">#{j.customer_id}</span></td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs">{j.gateway_name}</td>
                  <td className="px-4 py-2.5 text-slate-300 text-xs">{fmtTime(j.scheduled_time)}</td>
                  <td className="px-4 py-2.5 text-center text-slate-400 text-xs">{j.attempt_count}/{j.max_attempts}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={j.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AutoRbTab;
